import React, { useState, useEffect, useMemo } from 'react';
import { Badge, Row, Col, Button, ButtonGroup } from 'reactstrap';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import _ from 'lodash';
import { arrayMoveMutable } from 'array-move';
import { BrickForm, Draggable } from '..';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import { Utils } from '../../services';
import { useDispatch, useSelector } from 'react-redux';
import { setSmallDraggableCards, loadTags } from '../../redux';

import './BricksForm.css';

const BricksForm = ({ workflow, linkedResources, remove, updateBrickOrder, isLoading, changeId }) => {
    const dispatch = useDispatch();
    const { t }                   = useTranslation()
    const [collapse, setCollapse] = useState([]);
    const currency                = Utils.currency();

    const { KUserSettings } = useSelector(state => state.userSettings);
    const smallCards        = !!KUserSettings.smallDraggableCards;

    const bricks = workflow?.bricks || [];

    useEffect(() => {
        dispatch(loadTags());
    }, [dispatch]);

    const onCollapse = (id) => {
        let newValues = collapse;
        newValues[id] = !newValues[id];
        setCollapse(newValues);
    }

    const setUseSmallCards = useSmallCards => {
        dispatch(setSmallDraggableCards(!!useSmallCards));
    };

    const removeBrickAtPosition = (position) => {
        let temp = _.cloneDeep(collapse)
        temp.splice(position, 1);
        setCollapse(temp);
        remove(position);
    }

    const sortedBricks = useMemo(() => {
        return _.sortBy(bricks, 'position');
    }, [bricks])

    useEffect(() => {
        if (bricks?.length > 0)
            setCollapse(bricks?.map(p => false));
    }, [bricks]);

    const buildPreview = bricks => {
        const actions = (position) => {
            return (
                <Button color="danger" size="sm" onClick={() => { removeBrickAtPosition(position); }}>
                    <i className="fa fa-trash"></i>&nbsp;{t("common.delete")}
                </Button>
            );
        };

        const SortableTab = SortableElement(({ brick, sortIndex }) => {
            return (
                <Col sm={smallCards ? 6 : 12} lg={smallCards ? 3 : 6} key={brick.position} className="mb-4" >
                    <Draggable
                        id={brick.position}
                        onCollapse={onCollapse}
                        collapsed={collapse[brick.position] ?? false}
                        draggable
                        cardbody={ <BrickForm workflow={workflow} linkedResources={linkedResources} brick={brick} smallCard={!smallCards} isLoading={isLoading} /> }
                        title={ <b>{brick.position + 1} - {t(`workflows.bricks.${brick.type}.title`)}</b> }
                        actions={actions(brick.position)}
                    />
                </Col>)
        });

        const SortableGrid = SortableContainer(({ bricks }) => {
            return (
                <Row className="sortable-grid">
                    {(bricks || []).map((brick, index) => {
                        return <SortableTab key={`item-${index}`} index={index} brick={brick} sortIndex={brick.position} />;
                    })}
                </Row>
            );
        });

        const onSortEnd = ({ oldIndex, newIndex }) => {
            let bricks = [...sortedBricks];
            let newCollapse = collapse;
            arrayMoveMutable(newCollapse, oldIndex, newIndex);
            arrayMoveMutable(bricks, oldIndex, newIndex);
            bricks = bricks.map((p, i) => {
                p.position = i;
                return p;
            });
            updateBrickOrder(bricks);
            setCollapse(newCollapse);
        };

        return <SortableGrid
            bricks={bricks}
            axis="xy"
            onSortEnd={onSortEnd}
            useDragHandle={true}
            useWindowAsScrollContainer
            helperClass="sortableHelper"
        />
    };

    const brickComponent = useMemo(() => {
        return buildPreview(sortedBricks)
    }, [sortedBricks, smallCards])

    return (
        <div className="BricksForm">
            <Row className='px-3'>
                <Col>
                    <ButtonGroup className="float-right">
                        <Button style={{height: "33.5px"}} color={smallCards ? 'primary' : 'dark'} onClick={() => setUseSmallCards(true)}>
                            <i className='fa fa-th'></i>
                        </Button>
                        <Button style={{height: "33.5px"}} color={!smallCards ? 'primary' : 'dark'} onClick={() => setUseSmallCards(false)}>
                            <i className='fa fa-th-large'></i>
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            {sortedBricks.length > 0 &&
                brickComponent
            }
        </div>
    );
}

export default BricksForm;
